import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Tags, ToggleLeft, ToggleRight } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: '', discount_type: 'percent', discount_value: '', min_order: '0', max_uses: '0', active: true });

  useEffect(() => { fetchCoupons(); }, []);

  const fetchCoupons = async () => {
    try { const r = await axios.get(`${API}/admin/coupons`); setCoupons(r.data.coupons || []); } catch (e) {}
  };

  const saveCoupon = async () => {
    if (!form.code || !form.discount_value) { toast.error('נא למלא קוד והנחה'); return; }
    try {
      if (editing) {
        await axios.patch(`${API}/admin/coupons/${editing}`, { ...form, discount_value: parseFloat(form.discount_value), min_order: parseFloat(form.min_order || 0), max_uses: parseInt(form.max_uses || 0) });
        toast.success('קופון עודכן');
      } else {
        await axios.post(`${API}/admin/coupons`, { ...form, discount_value: parseFloat(form.discount_value), min_order: parseFloat(form.min_order || 0), max_uses: parseInt(form.max_uses || 0) });
        toast.success('קופון נוצר');
      }
      setAdding(false); setEditing(null);
      setForm({ code: '', discount_type: 'percent', discount_value: '', min_order: '0', max_uses: '0', active: true });
      fetchCoupons();
    } catch (e) { toast.error(e.response?.data?.detail || 'שגיאה'); }
  };

  const toggleActive = async (coupon) => {
    try {
      await axios.patch(`${API}/admin/coupons/${coupon.id}`, { active: !coupon.active });
      fetchCoupons();
    } catch (e) { toast.error('שגיאה'); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('למחוק קופון?')) return;
    try { await axios.delete(`${API}/admin/coupons/${id}`); toast.success('קופון נמחק'); fetchCoupons(); } catch (e) { toast.error('שגיאה'); }
  };

  const startEdit = (c) => {
    setEditing(c.id); setAdding(true);
    setForm({ code: c.code, discount_type: c.discount_type, discount_value: c.discount_value, min_order: c.min_order || 0, max_uses: c.max_uses || 0, active: c.active });
  };

  return (
    <div dir="rtl" data-testid="admin-coupons-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-black text-[#2C3E2D]" style={{ fontFamily: 'Heebo, sans-serif' }}>
          <Tags className="w-5 h-5 inline ml-2" />ניהול קופונים
        </h2>
        <button onClick={() => { setAdding(true); setEditing(null); setForm({ code: '', discount_type: 'percent', discount_value: '', min_order: '0', max_uses: '0', active: true }); }} className="bg-[#D97706] hover:bg-[#B45309] text-white rounded-xl px-4 py-2 text-sm font-bold flex items-center gap-2" data-testid="add-coupon-btn">
          <Plus className="w-4 h-4" /> הוסף קופון
        </button>
      </div>

      {adding && (
        <div className="bg-white rounded-xl p-4 mb-4 border border-stone-200 shadow-sm" data-testid="coupon-form">
          <h3 className="font-bold mb-3">{editing ? 'עריכת קופון' : 'קופון חדש'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-stone-400 block mb-1">קוד קופון</label>
              <input placeholder="למשל: SAVE10" value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} className="border rounded-lg px-3 py-2 text-sm w-full font-mono" dir="ltr" data-testid="coupon-code-input" />
            </div>
            <div>
              <label className="text-xs text-stone-400 block mb-1">סוג הנחה</label>
              <select value={form.discount_type} onChange={e => setForm({...form, discount_type: e.target.value})} className="border rounded-lg px-3 py-2 text-sm w-full" data-testid="coupon-type-select">
                <option value="percent">אחוז (%)</option>
                <option value="fixed">סכום קבוע (₪)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-stone-400 block mb-1">{form.discount_type === 'percent' ? 'אחוז הנחה' : 'סכום הנחה (₪)'}</label>
              <input type="number" placeholder={form.discount_type === 'percent' ? '10' : '50'} value={form.discount_value} onChange={e => setForm({...form, discount_value: e.target.value})} className="border rounded-lg px-3 py-2 text-sm w-full" data-testid="coupon-value-input" />
            </div>
            <div>
              <label className="text-xs text-stone-400 block mb-1">מינימום הזמנה (₪)</label>
              <input type="number" placeholder="0" value={form.min_order} onChange={e => setForm({...form, min_order: e.target.value})} className="border rounded-lg px-3 py-2 text-sm w-full" />
            </div>
            <div>
              <label className="text-xs text-stone-400 block mb-1">מקסימום שימושים (0 = ללא הגבלה)</label>
              <input type="number" placeholder="0" value={form.max_uses} onChange={e => setForm({...form, max_uses: e.target.value})} className="border rounded-lg px-3 py-2 text-sm w-full" />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 text-sm pb-2">
                <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} />
                פעיל
              </label>
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={saveCoupon} className="bg-green-600 hover:bg-green-700 text-white rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1" data-testid="coupon-save-btn"><Save className="w-4 h-4" /> שמור</button>
            <button onClick={() => { setAdding(false); setEditing(null); }} className="bg-stone-200 hover:bg-stone-300 rounded-lg px-4 py-2 text-sm font-bold flex items-center gap-1"><X className="w-4 h-4" /> ביטול</button>
          </div>
        </div>
      )}

      {coupons.length === 0 && !adding && (
        <div className="bg-white rounded-xl p-8 border border-stone-200 text-center text-stone-400">
          <Tags className="w-10 h-10 mx-auto mb-2 text-stone-300" />
          <p className="font-bold">אין קופונים עדיין</p>
          <p className="text-sm">לחץ "הוסף קופון" ליצור את הראשון</p>
        </div>
      )}

      <div className="space-y-2">
        {coupons.map(c => (
          <div key={c.id} className="bg-white rounded-xl p-4 border border-stone-100 flex items-center gap-4" data-testid={`coupon-row-${c.id}`}>
            <button onClick={() => toggleActive(c)} className="flex-shrink-0" data-testid={`coupon-toggle-${c.id}`}>
              {c.active ? <ToggleRight className="w-8 h-8 text-green-500" /> : <ToggleLeft className="w-8 h-8 text-stone-300" />}
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono font-black text-lg text-[#2C3E2D] bg-stone-100 px-3 py-0.5 rounded">{c.code}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${c.active ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-400'}`}>
                  {c.active ? 'פעיל' : 'מושבת'}
                </span>
              </div>
              <p className="text-xs text-stone-400 mt-1">
                {c.discount_type === 'percent' ? `${c.discount_value}% הנחה` : `₪${c.discount_value} הנחה`}
                {c.min_order > 0 && ` | מינימום ₪${c.min_order}`}
                {c.max_uses > 0 && ` | ${c.used_count || 0}/${c.max_uses} שימושים`}
              </p>
            </div>
            <button onClick={() => startEdit(c)} className="p-2 hover:bg-stone-100 rounded-lg"><Pencil className="w-4 h-4 text-stone-400" /></button>
            <button onClick={() => deleteCoupon(c.id)} className="p-2 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-400" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminCoupons;
